import { useForm } from 'react-hook-form';

import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { FormBlock } from '@/components/FormBlock';
import { MAX_UPLOADS } from '@/env';
import { useReviewMode } from '@/hooks/useReviewMode';
import { useZustandSync } from '@/hooks/useZustandSync';
import useProductStore from '@/stores/productStore';

import { Uploader } from '../form/uploader';
import ReviewFiles from './ReviewMode/ReviewFiles';

const deliverableArraySchema = z.array(
  z.object({
    id: z.string(),
    deliverableId: z.string(),
    filename: z.string(),
    hash: z.string(),
    size: z.number().optional()
  })
);

const deliverableSchemaRequired = z.object({
  deliverables: deliverableArraySchema
    .min(1, 'Você precisa enviar pelo menos um arquivo.')
    .max(MAX_UPLOADS, `Você pode enviar no máximo ${MAX_UPLOADS} arquivos.`)
});

const deliverableSchemaAllowEmpty = z.object({
  deliverables: deliverableArraySchema
});

type DeliverableFormValues = z.infer<typeof deliverableSchemaRequired>;

const FIELD_NAME = 'deliverables';

export type FileProps = {
  atLeastOneFile?: boolean;
};

export default function Files({ atLeastOneFile = true }: FileProps) {
  const form = useForm<DeliverableFormValues>({
    resolver: zodResolver(atLeastOneFile ? deliverableSchemaRequired : deliverableSchemaAllowEmpty),
    mode: 'onChange',
    defaultValues: { [FIELD_NAME]: useProductStore(d => d[FIELD_NAME]) }
  });

  useZustandSync<DeliverableFormValues>(form.control, data => {
    return {
      [FIELD_NAME]: data[FIELD_NAME]?.map((f: any) => ({
        hash: f.hash,
        filename: f.filename,
        id: f.id,
        deliverableId: f.deliverableId,
        ...(f.size !== undefined && { size: f.size })
      }))
    };
  });

  const files = useProductStore(d => d[FIELD_NAME]);
  const reviewMode = useReviewMode();

  return (
    <FormBlock
      name='files'
      form={form}
      title={reviewMode ? 'Arquivos' : 'Enviar arquivos'}
      lengthIndicator={`${files.length}/${MAX_UPLOADS}`}
      reviewWarning={files.length === 0}
      reviewWarningTooltip='Você não enviou nenhum arquivo, este produto será cadastrado como rascunho até que o cadastro seja concluído.'
      preview={<ReviewFiles />}
    >
      <Uploader name={FIELD_NAME} />
    </FormBlock>
  );
}
